import React from 'react';
import Button from '@mui/joy/Button';
import Input from '@mui/joy/Input';
import IconButton from '@mui/joy/IconButton';
import Stack from '@mui/joy/Stack';
import Typography from '@mui/joy/Typography';
import FormControl from '@mui/joy/FormControl';
import FormLabel from '@mui/joy/FormLabel';
import CircularProgress from '@mui/joy/CircularProgress';
import Tooltip from '@mui/joy/Tooltip';
import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import SendIcon from '@mui/icons-material/Send';
import { NotificationService } from '../../../common/notification';
import { botsSendSkinsApi } from '../../bots/botsApi';
import { BotTransferRequest, BotTransferResult } from '../../../models/bot';
import { OrderDetailsInfo, OrderStatus, OrderType } from '../../../models/order';

const transferableStatuses = [OrderStatus.Completed, OrderStatus.Failed, OrderStatus.ManualMode];

type DefaultProps = {
    order: OrderDetailsInfo,
    onCompleted: () => void
}

/** Ручная передача скинов заказа, оставшихся на боте */
export function OrderSkinsTransfer(props: DefaultProps) {
    const {order, onCompleted} = props;

    const [open, setOpen] = React.useState(false);
    const [tradeLink, setTradeLink] = React.useState('');
    const [isSending, setIsSending] = React.useState(false);
    const [resultOpen, setResultOpen] = React.useState(false);
    const [transferResults, setTransferResults] = React.useState<BotTransferResult[]>([]);

    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('md'));

    const sendSkins = async () => {
        if (!order.botId) {
            return;
        }

        setIsSending(true);
        setTransferResults([]);
        try {
            const request = new BotTransferRequest(order.botId, order.gameId, tradeLink, undefined, undefined, order.id);
            const result = await botsSendSkinsApi([request]);
            if (result.result) {
                setTransferResults(result.value ?? []);
                setOpen(false);
                setResultOpen(true);
            } else {
                NotificationService.alertError("Пожалуйста попробуйте позже или обратитесь в службу поддержки")
            }
        } finally {
            setIsSending(false);
        }
    }

    const copyOfferLink = async (link: string) => {
        try {
            await navigator.clipboard.writeText(link);
            NotificationService.alertSuccess("Скопировано!");
        } catch {
            NotificationService.alertError("Не удалось скопировать");
        }
    }

    // Передача доступна только для заказов через бота в конечном или ручном статусе
    const canTransfer = order.skinsLeftOnBot
        && order.type !== OrderType.P2P
        && transferableStatuses.includes(order.status);

    // Заказ перезагружается после закрытия результатов, иначе загрузка страницы скроет диалог
    const closeResults = () => {
        setResultOpen(false);
        onCompleted();
    }

    return (
        <React.Fragment>
            {canTransfer &&
                <Tooltip title={order.botId ? 'Передать скины' : 'Передать скины: у заказа не указан бот'}>
                    <span>
                        <IconButton size="sm" variant="outlined" color="primary"
                                    aria-label="Передать скины"
                                    disabled={!order.botId}
                                    onClick={() => setOpen(true)}>
                            <SendIcon/>
                        </IconButton>
                    </span>
                </Tooltip>}

            <Dialog
                open={open}
                fullScreen={fullScreen}
                onClose={() => setOpen(false)}
            >
                <DialogTitle>Передача скинов</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Скины заказа #{order.id}, оставшиеся на боте #{order.botId}, будут переданы получателю. Укажите торговую ссылку получателя.
                    </DialogContentText>
                    {isSending
                        ? <CircularProgress/>
                        : <Box
                            noValidate
                            component="form"
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                width: '100%',
                            }}
                        >
                            <FormControl sx={{mt: 2, minWidth: '100%', flex: 1}} size="sm">
                                <FormLabel>Торговая ссылка получателя</FormLabel>
                                <Input size="sm" placeholder="Tradelink"
                                       value={tradeLink} onChange={(event) => setTradeLink(event.target.value)}/>
                            </FormControl>
                        </Box>}
                </DialogContent>
                <DialogActions>
                    <Button color="neutral" onClick={() => setOpen(false)}>
                        Закрыть
                    </Button>
                    <Button color="primary" onClick={sendSkins}
                            disabled={isSending || tradeLink.trim().length === 0}>
                        Передать
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={resultOpen}
                fullScreen={fullScreen}
                onClose={closeResults}
            >
                <DialogTitle>Результаты передачи</DialogTitle>
                <DialogContent>
                    <Box sx={{display: 'flex', flexDirection: 'column', width: '100%'}}>
                        {transferResults.length === 0 &&
                            <Typography level="body-xs">Нет результатов</Typography>}
                        {transferResults.map((row, index) => {
                            const offerLink = `https://steamcommunity.com/tradeoffer/${row.steamOfferId}`;
                            return (
                                <Stack key={index} spacing={.5} direction={'row'} alignItems={'center'} sx={{mt: 1}}>
                                    <Typography level="body-xs">Бот #{row.botId}</Typography>
                                    {row.success
                                        ? (row.steamOfferId ?? 0) > 0
                                            ? <Input size="sm" readOnly value={offerLink} sx={{flex: 1}}
                                                     endDecorator={
                                                         <IconButton size="sm" aria-label="Копировать"
                                                                     onClick={() => copyOfferLink(offerLink)}>
                                                             <ContentCopyIcon/>
                                                         </IconButton>
                                                     }/>
                                            : <Typography level="body-xs">Обмен был создан, но ссылку на обмен получить не удалось.</Typography>
                                        : <Typography level="body-xs">Ошибка создания обмена: {row.errorMessage ?? 'Unhandled error'}</Typography>}
                                </Stack>
                            );
                        })}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button color="neutral" onClick={closeResults}>Закрыть</Button>
                </DialogActions>
            </Dialog>
        </React.Fragment>
    );
}
